import { gql, useLazyQuery } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomerDeleteModal,
  CustomerDeletePreCheckResult,
} from '~components/customers/CustomerDeleteModal';
import { fromError } from '~utils/errors';
import { MaybePromise } from '~utils/types';
import {
  CustomerDeletePreCheckDocument,
  CustomerDeletePreCheckQuery,
  CustomerDeletePreCheckQueryVariables,
  DeleteCustomer_CustomerFragment,
  useDeleteCustomerMutation,
} from './__generated__/DeleteCustomer.graphql';

gql`
  mutation DeleteCustomer($input: CustomerDeleteInput!) {
    customerDelete(input: $input) {
      customerId
    }
  }
`;

gql`
  query CustomerDeletePreCheck($customerId: ID!) {
    customerDeletePreCheck(customerId: $customerId) {
      blockingReasons
      deletableItems
    }
  }
`;

gql`
  fragment DeleteCustomer_customer on Customer {
    id
    name
  }
`;

interface UseDeleteCustomerProps {
  onCompleted?: () => MaybePromise<void>;
}

export function useDeleteCustomer({ onCompleted }: UseDeleteCustomerProps) {
  const toast = useToast();
  const { t } = useTranslation();
  const [deleteCustomer] = useDeleteCustomerMutation();
  const [runPreCheck] = useLazyQuery<
    CustomerDeletePreCheckQuery,
    CustomerDeletePreCheckQueryVariables
  >(CustomerDeletePreCheckDocument);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState<DeleteCustomer_CustomerFragment | null>(null);
  const [preCheck, setPreCheck] = useState<CustomerDeletePreCheckResult>({
    blockingReasons: [],
    deletableItems: [],
  });

  const askConfirmation = useCallback(
    async (target: DeleteCustomer_CustomerFragment) => {
      setCustomer(target);
      const { data } = await runPreCheck({ variables: { customerId: target.id } });
      setPreCheck(data?.customerDeletePreCheck ?? { blockingReasons: [], deletableItems: [] });
      setIsOpen(true);
    },
    [runPreCheck],
  );

  const handleConfirm = useCallback(async () => {
    if (!customer) return;
    setIsLoading(true);
    try {
      await deleteCustomer({ variables: { input: { customerId: customer.id } } });
      toast({
        status: 'success',
        title: t('customerDeleted', { name: customer.name }),
        description: t('customerDeletedDesc'),
      });
      setIsOpen(false);
      await onCompleted?.();
    } catch (err) {
      toast({
        status: 'error',
        title: t('deleteCustomerError'),
        description: fromError(err, 'DeleteCustomer', {
          CUSTOMER_DELETION_BLOCKED: t('deleteCustomerError'),
        }),
      });
    } finally {
      setIsLoading(false);
    }
  }, [customer, deleteCustomer, onCompleted, t, toast]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const confirmationNode = customer ? (
    <CustomerDeleteModal
      isOpen={isOpen}
      isLoading={isLoading}
      customerName={customer.name}
      preCheck={preCheck}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return {
    mutation: deleteCustomer,
    action: { askConfirmation, confirmationNode },
  };
}
